/**
 * Roost Planning page state (US 35621), shared by web and mobile. A scribe seat writes a doc
 * through the bridge (`ryn_write_planning_doc`); this store polls the index every 15 s and
 * refetches the open doc only when its `updated` stamp moves, so readers see edits within
 * seconds without re-downloading an unchanged doc.
 */
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import { RynBridgeService } from '../services/ryn-bridge.service';
import {
  ParsedPlanningDoc,
  parsePlanningMarkdown,
} from './planning-markdown.logic';

export const PLANNING_POLL_MS = 15_000;
const REQUEST_TIMEOUT_MS = 20_000;

/** One row of `GET /api/planning-docs`. */
export interface PlanningDocSummary {
  id: string;
  title: string;
  /** ISO UTC. */
  updated: string;
  size: number;
}

/** `GET /api/planning-docs/{id}`. */
export interface PlanningDoc extends PlanningDocSummary {
  markdown: string;
}

/**
 * True when the open doc must be (re)loaded: nothing loaded yet, a different doc selected, or
 * the index reports a newer `updated` than the copy on screen. A selected id missing from the
 * index keeps what is shown (the next poll may bring it back).
 */
export function docNeedsRefetch(
  selectedId: string | null,
  loaded: Pick<PlanningDoc, 'id' | 'updated'> | null,
  index: PlanningDocSummary[] | null
): boolean {
  if (!selectedId) return false;
  if (!loaded || loaded.id !== selectedId) return true;
  const row = index?.find((d) => d.id === selectedId);
  return row !== undefined && row.updated !== loaded.updated;
}

/** Newest first, then title, so the list order is stable between polls. */
export function sortPlanningIndex(
  docs: PlanningDocSummary[]
): PlanningDocSummary[] {
  return [...docs].sort(
    (a, b) =>
      b.updated.localeCompare(a.updated) || a.title.localeCompare(b.title)
  );
}

function errorText(error: unknown): string {
  if (error instanceof HttpErrorResponse)
    return `Bridge ${error.status || 'unreachable'}`;
  return error instanceof Error ? error.message : 'Request failed';
}

/** Provide per page (`providers: [PlanningDocsStore]`) so the poll lives and dies with the page. */
@Injectable()
export class PlanningDocsStore {
  private readonly http = inject(HttpClient);
  private readonly bridge = inject(RynBridgeService);
  private timer: ReturnType<typeof setInterval> | null = null;
  private inFlight = false;

  readonly docs = signal<PlanningDocSummary[] | null>(null);
  readonly selectedId = signal<string | null>(null);
  readonly doc = signal<PlanningDoc | null>(null);
  readonly error = signal<string | null>(null);
  readonly lastCheckedAt = signal<number | null>(null);
  readonly parsed = computed<ParsedPlanningDoc>(() =>
    parsePlanningMarkdown(this.doc()?.markdown)
  );
  /** The open doc, only once it matches the selection (never another doc's text under a new title). */
  readonly openDoc = computed(() => {
    const doc = this.doc();
    return doc && doc.id === this.selectedId() ? doc : null;
  });

  start(): void {
    if (this.timer) return;
    void this.refresh();
    this.timer = setInterval(() => void this.refresh(), PLANNING_POLL_MS);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  select(id: string | null): void {
    if (id === this.selectedId()) return;
    this.selectedId.set(id);
    void this.refreshDoc();
  }

  /** One poll: the index, then the open doc only if it changed. Overlapping polls are skipped. */
  async refresh(): Promise<void> {
    if (this.inFlight) return;
    this.inFlight = true;
    try {
      this.docs.set(sortPlanningIndex(await this.loadIndex()));
      this.lastCheckedAt.set(Date.now());
      this.error.set(null);
      await this.refreshDoc();
    } catch (error) {
      this.error.set(errorText(error));
    } finally {
      this.inFlight = false;
    }
  }

  private async refreshDoc(): Promise<void> {
    const id = this.selectedId();
    if (!docNeedsRefetch(id, this.doc(), this.docs()) || !id) return;
    try {
      const doc = await firstValueFrom(
        this.http
          .get<PlanningDoc>(
            `${this.base()}/planning-docs/${encodeURIComponent(id)}`
          )
          .pipe(timeout(REQUEST_TIMEOUT_MS))
      );
      // A slow response for a doc the reader has since left must not replace the new one.
      if (this.selectedId() === id) this.doc.set(doc);
    } catch (error) {
      if (this.selectedId() !== id) return;
      this.error.set(
        error instanceof HttpErrorResponse && error.status === 404
          ? `No planning doc "${id}"`
          : errorText(error)
      );
    }
  }

  private async loadIndex(): Promise<PlanningDocSummary[]> {
    try {
      const resp = await firstValueFrom(
        this.http
          .get<{ docs?: PlanningDocSummary[] }>(`${this.base()}/planning-docs`)
          .pipe(timeout(REQUEST_TIMEOUT_MS))
      );
      return resp?.docs ?? [];
    } catch (error) {
      // A bridge without the routes yet (Roost deployed first) has no docs, not a broken page.
      if (error instanceof HttpErrorResponse && error.status === 404) return [];
      throw error;
    }
  }

  private base(): string {
    return this.bridge.getBaseUrl().replace(/\/+$/, '');
  }
}
